import type { DashboardFeature } from '../../../services/dashboardService';
import type { Layout } from 'react-grid-layout';

/** Stored in DB when a row requires max_h; never passed to react-grid-layout. */
export const DASHBOARD_GRID_UNLIMITED_MAX_H = 9999;

export const ROWING_ARC_FEATURE_ID = 'rowing-arc';

/** Matches react-grid-layout rowHeight in PreviewArea. */
export const DASHBOARD_GRID_ROW_HEIGHT_PX = 28;

/** Vertical margin between grid rows (PreviewArea / SavedDashboardView). */
export const DASHBOARD_GRID_MARGIN_Y_PX = 16;

/**
 * Demo-tuned layout: full width, tall tile (coach dashboard d1ab8a70-039e-43fb-b0cd-68aabd404143).
 */
export const ROWING_ARC_DEFAULT_GRID_W = 16;
export const ROWING_ARC_DEFAULT_GRID_H = 55;

/** Builder min resize — a few rows below default; smaller tiles use inner scroll by design. */
export const ROWING_ARC_MIN_GRID_H = 50;

/** Builder guidance: rows above default before "too big" (resize still allowed). */
export const ROWING_ARC_MAX_GUIDANCE_GRID_H = ROWING_ARC_DEFAULT_GRID_H + 5;

/** Minimum tile width for readable arc + stroke sequence bar in the builder. */
export const ROWING_ARC_MIN_WIDTH_PX = 640;

/** Approximate pixel height for a grid row count (react-grid-layout). */
export function gridRowsToPixelHeight(rows: number): number {
  if (rows <= 0) return 0;
  return rows * DASHBOARD_GRID_ROW_HEIGHT_PX + (rows - 1) * DASHBOARD_GRID_MARGIN_Y_PX;
}

export const ROWING_ARC_IDEAL_HEIGHT_PX = gridRowsToPixelHeight(ROWING_ARC_DEFAULT_GRID_H);

const ROWING_ARC_WIDGET_SIZE_THRESHOLDS: WidgetSizeThresholds = {
  minHeight: gridRowsToPixelHeight(ROWING_ARC_MIN_GRID_H),
  minWidth: ROWING_ARC_MIN_WIDTH_PX,
  maxHeight: gridRowsToPixelHeight(ROWING_ARC_MAX_GUIDANCE_GRID_H),
};

/** Standard charts (power analysis, gate force, etc.) — tuned above old 14-row default. */
export const STANDARD_CHART_IDEAL_GRID_H = 18;
export const STANDARD_CHART_IDEAL_MIN_GRID_H = 17;
export const STANDARD_CHART_IDEAL_MAX_GRID_H = 19;
export const STANDARD_CHART_MIN_GRID_H = 12;
export const STANDARD_CHART_WARN_MAX_GRID_H = 24;
export const STANDARD_CHART_MAX_GUIDANCE_GRID_H = 28;

export interface WidgetSizeThresholds {
  minHeight: number;
  minWidth: number;
  maxHeight: number;
  /** Yellow when height is below the ideal band. */
  warnMinHeight?: number;
  idealMinHeight?: number;
  idealMaxHeight?: number;
  /** Yellow when height is above the ideal band but not yet too large. */
  warnMaxHeight?: number;
}

const STANDARD_CHART_WIDGET_SIZE_THRESHOLDS: WidgetSizeThresholds = {
  minHeight: gridRowsToPixelHeight(STANDARD_CHART_MIN_GRID_H),
  minWidth: 400,
  warnMinHeight: gridRowsToPixelHeight(STANDARD_CHART_IDEAL_MIN_GRID_H),
  idealMinHeight: gridRowsToPixelHeight(STANDARD_CHART_IDEAL_MIN_GRID_H),
  idealMaxHeight: gridRowsToPixelHeight(STANDARD_CHART_IDEAL_MAX_GRID_H),
  warnMaxHeight: gridRowsToPixelHeight(STANDARD_CHART_WARN_MAX_GRID_H),
  maxHeight: gridRowsToPixelHeight(STANDARD_CHART_MAX_GUIDANCE_GRID_H),
};

const CARD_OR_SETTINGS_FEATURES = new Set([
  'overview-cards',
  'performance-metric-cards',
  'parameter-panel',
  'roster-panel',
]);

/** Builder thumbnail: show too-small / too-big / good guidance. */
export function shouldValidateWidgetSize(_featureId: string): boolean {
  return true;
}

export function getWidgetSizeThresholds(featureId: string): WidgetSizeThresholds {
  if (featureId === ROWING_ARC_FEATURE_ID) {
    return ROWING_ARC_WIDGET_SIZE_THRESHOLDS;
  }
  return STANDARD_CHART_WIDGET_SIZE_THRESHOLDS;
}

export function isCardOrSettingsFeature(featureId: string): boolean {
  return CARD_OR_SETTINGS_FEATURES.has(featureId);
}

export interface IdealFeatureGridLayout {
  gridW: number;
  gridH: number;
  /** When set, snaps horizontal position (e.g. full-width Rowing Arc). */
  gridX?: number;
}

/** Recommended builder tile size per widget (matches createDashboardFeature defaults). */
export function getIdealFeatureGridLayout(featureId: string): IdealFeatureGridLayout {
  if (featureId === ROWING_ARC_FEATURE_ID) {
    return {
      gridW: ROWING_ARC_DEFAULT_GRID_W,
      gridH: ROWING_ARC_DEFAULT_GRID_H,
      gridX: 0,
    };
  }
  if (isCardOrSettingsFeature(featureId)) {
    return { gridW: 8, gridH: 8 };
  }
  return { gridW: 8, gridH: STANDARD_CHART_IDEAL_GRID_H };
}

/** Drop legacy height caps so react-grid-layout does not limit resize. */
export function normalizeDashboardFeature(feature: DashboardFeature): DashboardFeature {
  const { maxH: _maxH, ...rest } = feature;
  return rest;
}

export function normalizeDashboardFeatures(features: DashboardFeature[]): DashboardFeature[] {
  return features.map(normalizeDashboardFeature);
}

/** Initial tile position: stack below existing widgets; pair half-width charts on one row. */
export function getInitialFeaturePosition(
  featureId: string,
  existingFeatures: DashboardFeature[]
): { gridX: number; gridY: number } {
  const ideal = getIdealFeatureGridLayout(featureId);
  if (existingFeatures.length === 0) {
    return { gridX: ideal.gridX ?? 0, gridY: 0 };
  }

  const maxBottom = existingFeatures.reduce(
    (max, feature) => Math.max(max, feature.gridY + feature.gridH),
    0
  );

  if (ideal.gridX !== undefined || ideal.gridW >= 16) {
    return { gridX: ideal.gridX ?? 0, gridY: maxBottom };
  }

  const last = existingFeatures[existingFeatures.length - 1];
  if (last.gridW <= 8 && ideal.gridW <= 8 && last.gridX + last.gridW + ideal.gridW <= 16) {
    return { gridX: last.gridX + last.gridW, gridY: last.gridY };
  }

  return { gridX: 0, gridY: maxBottom };
}

export function createDashboardFeature(
  featureId: string,
  displayOrder: number,
  existingFeatures: DashboardFeature[] = []
): DashboardFeature {
  const ideal = getIdealFeatureGridLayout(featureId);
  const isRowingArc = featureId === ROWING_ARC_FEATURE_ID;
  const { gridX, gridY } = getInitialFeaturePosition(featureId, existingFeatures);

  return {
    id: `temp-${Date.now()}`,
    featureId,
    displayOrder,
    gridX,
    gridY,
    gridW: ideal.gridW,
    gridH: ideal.gridH,
    minW: isRowingArc ? 8 : 4,
    minH: isRowingArc ? ROWING_ARC_MIN_GRID_H : 6,
    maxW: 16,
    featureConfig: {},
  };
}

/** Layout item for react-grid-layout — no maxH (unlimited vertical resize). */
export function toReactGridLayoutItem(feature: DashboardFeature): Layout {
  return {
    i: feature.featureId,
    x: feature.gridX,
    y: feature.gridY,
    w: feature.gridW,
    h: feature.gridH,
    minW: feature.minW,
    minH: feature.minH,
    maxW: feature.maxW,
  };
}
